import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useResponsive } from "../../contexts/ResponsiveContext";
import "./landing_image.css";
import landingImage from "./landing_image.jpg";
import { BackgroundImage } from "../responsive-image/ResponsiveImage";
import enTranslations from "./en.json";
import deTranslations from "./de.json";

const LandingImage = () => {
  const { t, i18n } = useTranslation();
  const { isVeryWideScreen, buttonContainerWidth, shouldHideButtons } =
    useResponsive();
  const [headerHeight, setHeaderHeight] = useState(0);
  const [dimensions, setDimensions] = useState({
    landingHeight: "calc(100vh - 0px)", // Will be updated once header height is detected
    buttonHeight: "3.5vw",
    buttonFontSize: "1.6vw",
    textScale: 1,
  });

  i18n.addResourceBundle("en", "landingImage", enTranslations, true, true);
  i18n.addResourceBundle("de", "landingImage", deTranslations, true, true);

  useEffect(() => {
    const header = document.querySelector(".header");
    if (header) {
      setHeaderHeight(header.offsetHeight);
    }
  }, []);

  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const maxScrollThreshold = 100;
      const currentScroll = window.scrollY;
      const progress = Math.min(currentScroll / maxScrollThreshold, 1);
      setScrollProgress(progress);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const updateResponsiveDimensions = () => {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const aspectRatio = viewportWidth / viewportHeight;

      // Mobile breakpoint - keep simple and clean
      if (viewportWidth <= 768) {
        setDimensions({
          landingHeight: `calc(100vh - ${headerHeight}px)`,
          buttonHeight: "14vw", // Increased from 11vw for two lines
          buttonFontSize: "max(var(--min-button-text), 4vw)", // Use CSS variable
          textScale: 1,
        });
        return;
      }

      // Tablet breakpoint
      if (viewportWidth <= 1024) {
        const tabletRatio = (viewportWidth - 768) / (1024 - 768);
        const calculatedFontSize = 3 - tabletRatio * 1; // 3vw -> 2vw
        setDimensions({
          landingHeight: `calc(100vh - ${headerHeight}px)`,
          buttonHeight: `${12 - tabletRatio * 4}vw`, // Increased: 12vw -> 8vw
          buttonFontSize: `max(var(--min-button-text), ${calculatedFontSize}vw)`, // Use CSS variable
          textScale: 1,
        });
        return;
      }

      // Desktop - smooth calculations based on aspect ratio
      const minAspectRatio = 0.8;
      const maxAspectRatio = 1.6;
      const veryWideAspectRatio = 2.5;

      // Clamp aspect ratio for calculations
      const clampedRatio = Math.max(
        minAspectRatio,
        Math.min(aspectRatio, veryWideAspectRatio)
      );

      // Calculate normalized position between min and max (0 to 1)
      const normalizedRatio =
        (clampedRatio - minAspectRatio) / (maxAspectRatio - minAspectRatio);
      const clampedNormalized = Math.max(0, Math.min(1, normalizedRatio));

      // Very wide screens adjustment
      let wideScreenFactor = 1;
      if (aspectRatio > maxAspectRatio) {
        wideScreenFactor =
          1 -
          ((aspectRatio - maxAspectRatio) /
            (veryWideAspectRatio - maxAspectRatio)) *
            0.2;
        wideScreenFactor = Math.max(0.8, wideScreenFactor);
      }

      // Always use 100% screen height for landing image
      const landingHeight = 100;

      // Smooth interpolation for button height - INCREASED for two-line text
      const minButtonHeight = 6; // Increased from 4.5
      const maxButtonHeight = 4.5; // Increased from 3
      const buttonHeight =
        (minButtonHeight +
          (maxButtonHeight - minButtonHeight) * clampedNormalized) *
        wideScreenFactor;

      // Smooth interpolation for button font size - use CSS variables from main.css
      const minFontSize = 1.8; // Decreased from 2
      const maxFontSize = 1.2; // Decreased from 1.4
      const fontSize =
        (minFontSize + (maxFontSize - minFontSize) * clampedNormalized) *
        wideScreenFactor;

      // Text scale based on viewport width
      const minTextScale = 0.85;
      const maxTextScale = 1;
      const textScale =
        minTextScale + (maxTextScale - minTextScale) * clampedNormalized;

      setDimensions({
        landingHeight: `calc(100vh - ${headerHeight}px)`,
        buttonHeight: `${buttonHeight}vw`,
        buttonFontSize: `max(var(--min-button-text), ${fontSize}vw)`, // Use CSS variable
        textScale,
      });
    };

    updateResponsiveDimensions();
    window.addEventListener("resize", updateResponsiveDimensions);
    return () =>
      window.removeEventListener("resize", updateResponsiveDimensions);
  }, [headerHeight]); // Add headerHeight as dependency

  return (
    <div
      className="landing-container"
      style={{
        height: dimensions.landingHeight,
      }}
    >
      <div
        className="landing-image"
        role="img"
        aria-label={t("landingImage:landing_image_alt")}
      >
        <BackgroundImage
          src={landingImage}
          coverHeight={dimensions.landingHeight}
          loading="eager"
          fetchpriority="high"
        />
      </div>
      <div className="landing-content">
        <div
          className="text-overlay"
          style={{
            transform: `translateY(-50%) scale(${dimensions.textScale})`,
          }}
        >
          <span className="handwerk-text">{t("landingImage:handwerk")}</span>
          <span className="trifft-text">{t("landingImage:trifft")}</span>
        </div>
        <div
          className="moderne-text-overlay"
          style={{
            transform: `translateY(-50%) scale(${dimensions.textScale})`,
          }}
        >
          <span className="moderne-text">{t("landingImage:moderne")}</span>
        </div>
        <div
          className="button-container"
          style={{
            width:
              buttonContainerWidth === "100%"
                ? `${100 - scrollProgress * 4}%`
                : buttonContainerWidth,
            left: buttonContainerWidth === "100%" ? "50%" : "50%",
            right: buttonContainerWidth === "100%" ? "auto" : "auto",
            transform:
              buttonContainerWidth === "100%"
                ? "translateX(-50%)"
                : "translateX(-50%)",
            display: shouldHideButtons ? "none" : "flex",
          }}
        >
          <a
            href={
              i18n.language === "en" ? "/en/repair&restore" : "/repair&restore"
            }
            className="button focus-highlight"
            tabIndex={0}
            style={{
              borderTopLeftRadius: isVeryWideScreen
                ? "15px"
                : `${scrollProgress * 15}px`,
              height: dimensions.buttonHeight,
              fontSize: dimensions.buttonFontSize,
            }}
            aria-label={t("landingImage:repair_restore_aria_label")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                window.location.href =
                  i18n.language === "en"
                    ? "/en/repair&restore"
                    : "/repair&restore";
              }
            }}
          >
            <span>{t("landingImage:repair_restore")}</span>
          </a>
          <a
            href={i18n.language === "en" ? "/en/buy&rent" : "/buy&rent"}
            className="button focus-highlight"
            tabIndex={0}
            style={{
              height: dimensions.buttonHeight,
              fontSize: dimensions.buttonFontSize,
            }}
            aria-label={t("landingImage:buy_rent_aria_label")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                window.location.href =
                  i18n.language === "en" ? "/en/buy&rent" : "/buy&rent";
              }
            }}
          >
            <span>{t("landingImage:buy_rent")}</span>
          </a>
          <a
            href={
              i18n.language === "en"
                ? "/en/analyse&document"
                : "/analyse&document"
            }
            className="button focus-highlight"
            tabIndex={0}
            style={{
              borderTopRightRadius: isVeryWideScreen
                ? "15px"
                : `${scrollProgress * 15}px`,
              height: dimensions.buttonHeight,
              fontSize: dimensions.buttonFontSize,
            }}
            aria-label={t("landingImage:analyse_document_aria_label")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                window.location.href =
                  i18n.language === "en"
                    ? "/en/analyse&document"
                    : "/analyse&document";
              }
            }}
          >
            <span>{t("landingImage:analyse_document")}</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default LandingImage;
